import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { message } from "antd";
import APIURL, { IMAGE_BASE_URL } from "../api/apiClient";

const resolveCategoryImage = (path) => {
  if (!path) {
    return "";
  }

  return path.startsWith("http") ? path : `${IMAGE_BASE_URL}${String(path).replace(/^\/+/, "")}`;
};

const normalizeApiResponse = (response) => {
  if (typeof response !== "string") {
    return response;
  }

  try {
    return JSON.parse(response.trim() || "{}");
  } catch {
    return { success: false, message: response };
  }
};

const CategoryEditDialog = ({ open, category, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    category_id: "",
    name: "",
    featured: "0",
    status: 1,
    thumbnail: null,
    currentImage: "",
    removeImage: false,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !category) {
      return;
    }

    setFormData({
      category_id: category.id,
      name: category.name || "",
      featured: String(category.featured ?? 0),
      status: Number(category.status ?? 1),
      thumbnail: null,
      currentImage: resolveCategoryImage(category.image),
      removeImage: false,
    });
    setPreviewUrl("");
  }, [open, category]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const activePreview = useMemo(
    () => previewUrl || (formData.removeImage ? "" : formData.currentImage),
    [formData.currentImage, formData.removeImage, previewUrl]
  );

  const handleClose = () => {
    if (submitting) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }

    onClose();
  };

  const handleChange = (event) => {
    const { name, value, type, checked, files } = event.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
      return;
    }

    if (type === "file") {
      const file = files?.[0] || null;

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }

      if (file) {
        setPreviewUrl(URL.createObjectURL(file));
      }

      setFormData((prev) => ({
        ...prev,
        thumbnail: file,
        removeImage: false,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }

    setFormData((prev) => ({
      ...prev,
      thumbnail: null,
      currentImage: "",
      removeImage: true,
    }));
  };

  const handleSubmit = async () => {
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      message.error("Category name is required.");
      return;
    }

    const payload = new FormData();
    payload.append("category_id", formData.category_id);
    payload.append("name", trimmedName);
    payload.append("status", formData.status);
    payload.append("featured", formData.featured);

    if (formData.removeImage) {
      payload.append("remove_image", "1");
    }

    if (formData.thumbnail instanceof File) {
      payload.append("thumbnail", formData.thumbnail);
    }

    setSubmitting(true);

    try {
      const response = await APIURL.post("/updatecategory.php", payload, true);
      const normalizedResponse = normalizeApiResponse(response);

      if (normalizedResponse?.success) {
        message.success(normalizedResponse.message || "Category updated successfully.");
        onSaved();
        handleClose();
        return;
      }

      message.error(normalizedResponse?.message || "Unable to update category.");
    } catch (error) {
      console.error("Category update failed:", error);
      message.error(error?.message || "Unable to update category.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Category</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Category Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            size="small"
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Category Image
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <Button variant="outlined" component="label" disabled={submitting}>
                {activePreview ? "Change Image" : "Upload Image"}
                <input
                  hidden
                  type="file"
                  name="thumbnail"
                  accept="image/png, image/jpeg, image/webp"
                  onChange={handleChange}
                />
              </Button>
              {activePreview ? (
                <Button color="error" onClick={handleRemoveImage} disabled={submitting}>
                  Remove Image
                </Button>
              ) : null}
            </Stack>
            {activePreview ? (
              <Box
                sx={{
                  mt: 2,
                  width: 170,
                  height: 120,
                  overflow: "hidden",
                  borderRadius: 2,
                  border: "1px solid #d8dee4",
                  backgroundColor: "#f8fafc",
                }}
              >
                <img
                  src={activePreview}
                  alt={`${formData.name || "Category"} preview`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                No category image selected.
              </Typography>
            )}
          </Box>

          <TextField
            select
            label="Featured"
            name="featured"
            value={formData.featured}
            onChange={handleChange}
            size="small"
            fullWidth
          >
            <MenuItem value="1">Computer</MenuItem>
            <MenuItem value="0">Laptop</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1">Status</Typography>
            <Switch
              checked={formData.status === 1}
              onChange={(event) =>
                handleChange({
                  target: {
                    name: "status",
                    type: "checkbox",
                    checked: event.target.checked,
                  },
                })
              }
              name="status"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryEditDialog;
